import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

export const SettingsAdmin = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        whatsapp: settings.whatsapp,
        address: settings.address,
        instagram: settings.instagram,
        weekly: settings.weekly,
      });
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <div className="flex items-center gap-3 text-sm text-espresso/55 py-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
      </div>
    );
  }

  const inputCls =
    "w-full bg-white border border-taupe/30 rounded-md px-4 py-3 text-sm text-espresso placeholder:text-espresso/40 focus:outline-none focus:ring-1 focus:ring-taupe transition-shadow duration-300";

  const setDay = (i, patch) =>
    setSettings({ ...settings, weekly: { ...settings.weekly, [i]: { ...settings.weekly[i], ...patch } } });

  return (
    <div className="space-y-8" data-testid="admin-settings-tab">
      <div className="bg-white border border-taupe/20 rounded-2xl p-6 space-y-4">
        <h3 className="font-display text-xl text-espresso">Coordonnées</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="text-xs tracking-[0.1em] uppercase text-espresso/55 flex flex-col gap-1.5">
            Numéro WhatsApp
            <input className={inputCls} data-testid="settings-whatsapp-input" value={settings.whatsapp || ""} onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })} />
          </label>
          <label className="text-xs tracking-[0.1em] uppercase text-espresso/55 flex flex-col gap-1.5">
            Adresse du salon
            <input className={inputCls} data-testid="settings-address-input" value={settings.address || ""} onChange={(e) => setSettings({ ...settings, address: e.target.value })} />
          </label>
          <label className="text-xs tracking-[0.1em] uppercase text-espresso/55 flex flex-col gap-1.5 sm:col-span-2">
            Lien Instagram (optionnel)
            <input className={inputCls} data-testid="settings-instagram-input" placeholder="https://instagram.com/…" value={settings.instagram || ""} onChange={(e) => setSettings({ ...settings, instagram: e.target.value })} />
          </label>
        </div>
      </div>

      <div className="bg-white border border-taupe/20 rounded-2xl p-6">
        <h3 className="font-display text-xl text-espresso mb-4">Horaires d'ouverture</h3>
        <div className="space-y-3" data-testid="settings-weekly">
          {DAYS.map((label, i) => {
            const d = settings.weekly?.[i] || { open: false, start: "09:00", end: "18:00" };
            return (
              <div key={i} className="flex flex-wrap items-center gap-4 py-2 border-b border-taupe/10 last:border-0">
                <label className="flex items-center gap-3 w-36 text-sm text-espresso">
                  <input
                    type="checkbox"
                    data-testid={`day-open-${i}`}
                    checked={!!d.open}
                    onChange={(e) => setDay(i, { open: e.target.checked })}
                    className="w-4 h-4 accent-[#A39171]"
                  />
                  {label}
                </label>
                {d.open && (
                  <>
                    <input type="time" data-testid={`day-start-${i}`} className={inputCls + " !w-auto"} value={d.start} onChange={(e) => setDay(i, { start: e.target.value })} />
                    <span className="text-espresso/40 text-sm">à</span>
                    <input type="time" data-testid={`day-end-${i}`} className={inputCls + " !w-auto"} value={d.end} onChange={(e) => setDay(i, { end: e.target.value })} />
                  </>
                )}
                {!d.open && <span className="text-sm text-espresso/40 italic">Fermé</span>}
              </div>
            );
          })}
        </div>
      </div>

      <button
        data-testid="settings-save-btn"
        onClick={save}
        disabled={saving}
        className="rounded-full bg-taupe text-cream px-8 py-4 text-sm tracking-[0.12em] uppercase inline-flex items-center gap-2 hover:bg-taupe-dark transition-colors duration-300 disabled:opacity-50"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.5} />}
        Enregistrer
      </button>
    </div>
  );
};
